import React, { useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import P from "@components/common/P/P";
import { useOutsideClick } from "hooks/useOutsideClick";
import SvgIcon from "@components/common/SvgIcon/SvgIcon";
import { ChevronRightIcon } from "@assets/icons";

const languages = [
	{ code: "ru", label: "Рус" },
	{ code: "en", label: "Eng" },
	{ code: "tr", label: "Tür" },
];

interface LanguageSelectorProps {
	isDropdownOpen: boolean;
	setIsDropdownOpen: (value: boolean) => void;
	selectedLanguage: { code: string; label: string };
	handleLanguageChange: (language: { code: string; label: string }) => void;
	isScrolled: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
	isDropdownOpen,
	setIsDropdownOpen,
	selectedLanguage,
	handleLanguageChange,
}) => {
	const dropdownRef = useRef<HTMLDivElement>(null);

	useOutsideClick(dropdownRef, () => {
		if (isDropdownOpen) setIsDropdownOpen(false);
	});

	return (
		<div ref={dropdownRef} className={`relative p-2 bg-shadeOne rounded-xl`}>
			<div
				onClick={() => setIsDropdownOpen(!isDropdownOpen)}
				className="grid gap-2 cursor-pointer grid-cols-maxTwo"
			>
				<P
					size="t1"
					type="medium"
					className={`uppercase cursor-pointer text-textColor`}
				>
					{selectedLanguage.label}
				</P>
				<SvgIcon
					Icon={ChevronRightIcon}
					width={18}
					height={18}
					stroke="currentColor"
				/>
			</div>

			<AnimatePresence>
				{isDropdownOpen && (
					<motion.div
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.3 }}
						className={`absolute right-0 w-24 px-1 py-1 mt-4 bg-white shadow-shadowOne`}
					>
						<ul className="grid grid-flow-row gap-2 auto-rows-max">
							{languages.map(({ code, label }) => (
								<li
									key={code}
									onClick={() => handleLanguageChange({ code, label })}
									className="flex items-center px-4 py-2 rounded-md cursor-pointer hover:bg-grayOne"
								>
									<P
										size="t1"
										type={
											code === selectedLanguage.code ? "semibold" : "medium"
										}
										className={`uppercase cursor-pointer text-textColor`}
									>
										{label}
									</P>
								</li>
							))}
						</ul>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};

export default React.memo(LanguageSelector);
